import React, { FC, useEffect } from "react";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { FormikErrors, FormikTouched } from "formik";

import {
  AutoCompleteInput,
  CheckBoxInput,
  DateInput,
  LabeledInput,
  RadioInput,
  SelectOptions,
} from "@/component/input";
import SwitchComponent from "@/component/input/switch";
import ListView from "@/component/list";
import ServiceUserSearch from "@/component/serviceUserSearch";
import TextEditorComponent from "@/component/texteditor";
import { getDailyNoteCategories, getDailyNotesTasks } from "@/services/site";
import { DAYTIME, FREQUENCIES } from "@/utils/constants";
import logger from "@/runtime-utils/observability/logger";
import { DayTime, TaskManagement } from "../../../interfaces/ITaskManagement";

type Props = {
  values: TaskManagement;
  setFieldValue: (field: keyof TaskManagement, value: any) => void;
  errors: FormikErrors<TaskManagement>;
  touched: FormikTouched<TaskManagement>;
  disabled?: boolean;
  type: "edit" | "add";
};

const InfoSection: FC<Props> = ({
  type,
  values,
  setFieldValue,
  errors,
  touched,
  disabled,
}) => {
  const [categories, setCategories] = React.useState<SelectOptions[]>([]);
  const [tasks, setTasks] = React.useState<SelectOptions[]>([]);

  const fetchCategories = async () => {
    try {
      const categoriesResponse = await getDailyNoteCategories();

      if (categoriesResponse.success) {
        setCategories(
          categoriesResponse.data.map((item: any) => ({
            value: item._id,
            label: item.title,
          }))
        );
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const fetchTasks = async (categoryId: string | undefined) => {
    const response = await getDailyNotesTasks(categoryId || "");
    if (response.success) {
      setTasks(
        response.data.map((item: any) => ({
          value: item._id,
          label: item.title,
        }))
      );
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchCategories();
  }, []);

  useEffect(() => {
    if (values.selectedCategory) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchTasks(values.selectedCategory?.value);
    }
  }, [values.selectedCategory]);

  const setSelectedDayTime = (time: DayTime) => {
    const newDayTime = [time];

    setFieldValue("dayTime", newDayTime);
  };

  const setTaskInterval = (frequency: string) => {
    setFieldValue("rrule", { frequency });
  };

  return (
    <Box className="personalCare">
      <Box className="dailydata">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormGroup>
              <DateInput
                label="Date"
                value={dayjs(values.startDate)}
                onChange={(value) => {
                  setFieldValue("startDate", value);
                }}
                disabled={disabled}
                // errorText={touched.startDate && errors.startDate}
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {type === "edit" && (
            <Grid size={12}>
              <FormGroup>
                <ServiceUserSearch
                  disabled={disabled}
                  multiple={false}
                  value={values.selectedUsers?.[0]}
                  onSelect={(val) => setFieldValue("selectedUsers", [val])}
                  inputProps={{
                    label: "Service User",
                    className: "personal",
                  }}
                />
              </FormGroup>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormGroup>
              <AutoCompleteInput
                disabled={disabled}
                multiple={false}
                options={categories}
                onSelectItem={(event: any, newValue: SelectOptions) => {
                  setFieldValue("selectedCategory", newValue);
                  fetchTasks(newValue.value);
                  setFieldValue("selectedTasks", []);
                }}
                value={values.selectedCategory}
                inputProps={{
                  label: "Category",
                  className: "personal",
                }}
              />
            </FormGroup>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormGroup>
              <AutoCompleteInput
                disabled={disabled}
                multiple={false}
                options={tasks}
                onSelectItem={(event: any, newValue: string) => {
                  setFieldValue("selectedTasks", [newValue]);
                }}
                value={values.selectedTasks?.[0]}
                inputProps={{
                  label: "Tasks",
                  className: "personal",
                }}
              />
            </FormGroup>
          </Grid>
          <Grid size={12}>
            <FormGroup>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "500",
                  fontSize: "16px",
                  lineHeight: "19px",
                  color: "#1F4970",
                  margin: "4px 0px",
                }}
              >
                Description*{" "}
              </Typography>
              <TextEditorComponent
                aria-label="minimum height"
                placeholder="type here"
                value={values.description}
                onChange={(value) => setFieldValue("description", value)}
                readOnly={disabled}
              />
              {touched.description && errors.description && (
                <FormHelperText error>{errors.description}</FormHelperText>
              )}
            </FormGroup>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormGroup
              sx={{
                "& .Mui-disabled .Mui-checked": {
                  color: "#ccc!important",
                },
              }}
            >
              <RadioInput
                disabled={disabled}
                value={values.idealTime}
                label="Select time"
                errorText={touched.idealTime && errors.idealTime}
                onChange={({ target }) => {
                  setFieldValue("idealTime", target.value);
                  if (target.value === "anytime") {
                    setFieldValue("startTime", "");
                    setFieldValue("dayTime", []);
                  } else if (target.value === "session") {
                    setFieldValue("startTime", "");
                  } else {
                    setFieldValue("dayTime", []);
                  }
                }}
                options={[
                  {
                    label: "Any time",
                    value: "anytime",
                  },
                  {
                    label: "Session",
                    value: "session",
                  },
                  {
                    label: "Time",
                    value: "specific-time",
                  },
                ]}
              />
            </FormGroup>
          </Grid>
          {values.idealTime === "specific-time" && (
            <Grid size={6}>
              <FormGroup>
                <LabeledInput
                  label="Time"
                  placeholder="04:53"
                  className="personal"
                  type="time"
                  value={values.startTime}
                  onChange={({ target }) => {
                    setFieldValue("startTime", target.value);
                  }}
                  disabled={disabled}
                  errorText={touched.startTime && errors.startTime}
                />
              </FormGroup>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* {values.idealTime === "session" && ( */}
      <Box className="mainSession">
        {values.idealTime === "session" && (
          <Box className="Visit">
            {DAYTIME.map((item) => (
              <Typography
                key={item.value}
                variant="h6"
                className={`weekData ${disabled && "disabled"} ${values.dayTime.includes(item.value) ? "active" : ""}`}
                sx={{
                  margin: "0px 7px",
                  border:
                    touched.dayTime && errors.dayTime
                      ? "1px solid red!important"
                      : "",
                  color:
                    touched.dayTime && errors.dayTime ? "red!important" : "",
                }}
                onClick={() => !disabled && setSelectedDayTime(item.value)}
              >
                {item.label}
              </Typography>
            ))}
          </Box>
        )}
        <FormHelperText error>
          {touched.dayTime && errors.dayTime}
        </FormHelperText>
        <Box>
          <CheckBoxInput
            disabled={disabled}
            onClick={() => {
              setFieldValue("recurring", !values.recurring);
              if (!values.recurring) {
                setFieldValue("rrule", {});
              }
            }}
            label="Recurring"
            checked={values.recurring}
            sx={{
              border: "0",
              fontWeight: "400",
              fontSize: "14px",
              fontFamily: "poppins",
            }}
          />
          {values.recurring && (
            <>
              <Typography className="frequency">Select frequency</Typography>
              <Box className="Visit">
                <ListView
                  data={FREQUENCIES}
                  renderItem={(el) => (
                    <Typography
                      variant="h6"
                      className={`weekData ${disabled && "disabled"} ${values.rrule.frequency === el.value ? "active" : ""}`}
                      sx={{
                        margin: "0px 7px",
                        border:
                          touched.rrule?.frequency && errors.rrule?.frequency
                            ? "1px solid red!important"
                            : "",
                        color:
                          touched.rrule?.frequency && errors.rrule?.frequency
                            ? "red!important"
                            : "",
                      }}
                      onClick={() => {
                        !disabled && setTaskInterval(el.value);
                      }}
                    >
                      {el.label}
                    </Typography>
                  )}
                />
              </Box>
              <FormHelperText error>
                {touched.rrule?.frequency && errors.rrule?.frequency}
              </FormHelperText>
            </>
          )}
        </Box>
      </Box>
      {/* )} */}
      {/* <Box className="add">
        <Typography variant="h6">
          Carers are required to submit a daily note upon completing their tasks
        </Typography>
        <SwitchComponent
          disabled={disabled}
          checked={values.noteSubmitRequired}
          onChange={(_, checked) =>
            setFieldValue("noteSubmitRequired", checked)
          }
        />
      </Box> */}
      <Box className="add">
        <Typography variant="h6">Show this task in the roster</Typography>
        <SwitchComponent
          disabled={disabled}
          checked={values.showInRoster}
          onChange={(_, checked) => setFieldValue("showInRoster", checked)}
        />
      </Box>
    </Box>
  );
};

export default InfoSection;
